/**
 * Slice 19d — listing-only variant of useAdsLibraryExampleBankAssist for the
 * "browse indexed ads to link more exemplars" picker. Selection is owned by
 * the parent (mirrors the template's `example_social_post_ids`) so the picker
 * does not maintain its own `selected` set.
 */
import { useCallback, useEffect, useState } from "react";
import { listAdsSocialPosts } from "@/lib/adsAssistantClientSocial";
import type { AdsAssistantSocialPostRow } from "@/lib/adsAssistantClientTypes";
import { fetchOrgLocations } from "@/lib/locations/fetchOrgLocations";
import {
  platformOptionsFromOrgChecklistConfig,
  type AdsPlatformSelectOption,
} from "@/lib/orgAdsPlatformOptions";
import { requireSupabase, supabase } from "@/lib/supabase";

export const PICKER_PAGE_SIZE = 10;

type LocationOpt = { id: string; name: string };

export function useAdsIndexedPostPicker(scopedOrgId: string | null) {
  const [platformOptions, setPlatformOptions] = useState<AdsPlatformSelectOption[]>(() =>
    platformOptionsFromOrgChecklistConfig(null),
  );
  const [locations, setLocations] = useState<LocationOpt[]>([]);
  const [platform, setPlatform] = useState("");
  const [locationId, setLocationId] = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [searchApplied, setSearchApplied] = useState("");
  const [page, setPage] = useState(0);
  const [posts, setPosts] = useState<AdsAssistantSocialPostRow[]>([]);
  const [total, setTotal] = useState(0);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  useEffect(() => {
    if (!scopedOrgId || !supabase) {
      setPlatformOptions(platformOptionsFromOrgChecklistConfig(null));
      setLocations([]);
      return;
    }
    const sb = requireSupabase();
    void (async () => {
      const { data: orgRow } = await sb
        .from("organizations")
        .select("sales_daily_checklist_config")
        .eq("id", scopedOrgId)
        .maybeSingle();
      const raw = (orgRow as { sales_daily_checklist_config?: unknown } | null)
        ?.sales_daily_checklist_config;
      setPlatformOptions(platformOptionsFromOrgChecklistConfig(raw));
      const { data: locRows } = await fetchOrgLocations(sb, scopedOrgId);
      setLocations((locRows as LocationOpt[]) ?? []);
    })();
  }, [scopedOrgId]);

  useEffect(() => {
    if (!platformOptions.length) return;
    setPlatform((p) => (p && platformOptions.some((o) => o.key === p) ? p : platformOptions[0]!.key));
  }, [platformOptions]);

  const loadPosts = useCallback(async () => {
    if (!scopedOrgId || !platform) return;
    setListLoading(true);
    setListError(null);
    const r = await listAdsSocialPosts({
      platform,
      location_id: locationId || undefined,
      search: searchApplied.trim() || undefined,
      limit: PICKER_PAGE_SIZE,
      offset: page * PICKER_PAGE_SIZE,
    });
    setListLoading(false);
    if (!r.ok) {
      setListError(r.error);
      setPosts([]);
      setTotal(0);
      return;
    }
    setPosts(r.data.posts ?? []);
    setTotal(typeof r.data.total === "number" ? r.data.total : 0);
  }, [scopedOrgId, platform, locationId, searchApplied, page]);

  useEffect(() => {
    void loadPosts();
  }, [loadPosts]);

  const resetPaging = useCallback(() => setPage(0), []);
  const pageCount = Math.max(1, Math.ceil(total / PICKER_PAGE_SIZE));

  return {
    platformOptions,
    locations,
    platform,
    setPlatform,
    locationId,
    setLocationId,
    searchDraft,
    setSearchDraft,
    searchApplied,
    setSearchApplied,
    page,
    setPage,
    posts,
    total,
    listLoading,
    listError,
    pageCount,
    resetPaging,
  };
}
